import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Rings } from "@/components/Rings";

const CHAPTERS = [
  {
    number: "One",
    title: "Two people, one island",
    body: [
      "Antelli is run by Kellie and Antonio from Bribie Island, a sand island off the coast of Queensland, about an hour north of Brisbane. It is a small company in the honest sense: two people, five brands, and a habit of doing things properly rather than quickly.",
      "There is no head office tower and no department of anything. The work happens from the island, around tides and ferry timetables, and goes out to readers, travellers and hosts all over the world.",
    ],
  },
  {
    number: "Two",
    title: "The name",
    body: [
      "Antelli is the two names put together — Antonio and Kellie. It also echoes anelli, the Italian word for rings: interlocking, connected, a family of things rather than one thing.",
      "That turned out to be the right picture for the company. Each brand is its own ring, with its own colour and its own voice. Antelli is simply the place where they overlap.",
    ],
  },
  {
    number: "Three",
    title: "What connects the work",
    body: [
      "The five brands look unrelated on paper — travel advice, honest hotel reviews, relocation guides, short-stay hosting and a small digital press. In practice they all come from the same few years: planning trips down to the last detail, living abroad and learning what it actually costs, and making a property earn its keep.",
      "Everything the group publishes or advises on has been done first by the people writing it. The line we work by is the one on the front page: dream it, plan it, live it. The dreaming is the easy part. The other two are where the work is, and they are the part we care about.",
    ],
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About — Antelli"
        description="Antelli is run by Kellie and Antonio from Bribie Island, Queensland. The story behind the name and the five brands."
      />
      <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-24 pb-24 sm:pt-36 sm:pb-36">
        <Reveal>
          <div className="flex items-center gap-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#7A6034]">About</p>
          </div>
          <h1
            data-testid="about-heading"
            className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-[#1A1C23] font-medium"
          >
            A small company, on purpose
          </h1>
          <figure className="mt-14">
            <img
              src="/logo-stacked.jpg"
              alt="The Antelli logo — founded by Kellie and Antonio"
              data-testid="about-emblem"
              className="w-full max-w-xs mix-blend-multiply"
            />
          </figure>
        </Reveal>

        <div className="mt-16 sm:mt-24 space-y-16 sm:space-y-24">
          {CHAPTERS.map((chapter, i) => (
            <Reveal key={chapter.number} delay={0.05}>
              <section data-testid={`about-chapter-${i + 1}`}>
                <p className="font-serif-display italic text-lg text-[#987E54]">
                  {chapter.number}
                </p>
                <h2 className="mt-3 font-serif-display text-2xl sm:text-3xl text-[#1A1C23]">
                  {chapter.title}
                </h2>
                <div className="mt-6 space-y-5">
                  {chapter.body.map((para, j) => (
                    <p key={j} className="text-base leading-relaxed text-[#4A4D57]">
                      {para}
                    </p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.05}>
          <div className="mt-20 sm:mt-28 border-t border-[#1A1C23]/10 pt-10 flex items-center justify-between">
            <p className="text-sm text-[#757882]">
              Kellie &amp; Antonio — Bribie Island, Queensland
            </p>
            <Rings size={26} />
          </div>
        </Reveal>
      </div>
    </div>
  );
}
